import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UploadCloud, FileSpreadsheet, Loader2, ArrowRight, ArrowLeft, AlertTriangle,
  CheckCircle2, Sparkles, ShieldAlert, BarChart2, List, Table2, Lock,
} from 'lucide-react';
import { LocalizedText } from '../types';
import { createSurvey, fetchSurveyQuestions } from '../services/surveyService';
import {
  ImportColumn, IMPORT_TYPES, ImportQuestionType, ImportBatchRow,
  inferImportSchema, columnsToQuestions, cellToAnswerValue, distinctValues,
  createImportRecord, setImportStatus, importResponsesBatch, embedPendingAnswers,
} from '../services/importService';

type Step = 'UPLOAD' | 'MAPPING' | 'IMPORTING' | 'DONE';

const MAX_ROWS = 20000;      // hard cap; anything beyond is truncated with a visible notice
const INSERT_BATCH = 100;    // rows per insert call
const EMBED_CHUNK = 100;     // answers per embed call

const TYPE_LABEL: Record<ImportQuestionType, string> = {
  TEXT: 'Text', MULTIPLE_CHOICE: 'Single choice', CHECKBOX: 'Multi-select', RATING: 'Rating', DATE: 'Date',
};

const ImportSurveyView: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('UPLOAD');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Parsed file
  const [filename, setFilename] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<any[][]>([]);
  const [parsing, setParsing] = useState(false);

  // Mapping
  const [columns, setColumns] = useState<ImportColumn[]>([]);
  const [title, setTitle] = useState<LocalizedText>({ en: '' });

  // Import progress
  const [phase, setPhase] = useState<'responses' | 'embedding'>('responses');
  const [respDone, setRespDone] = useState(0);
  const [respTotal, setRespTotal] = useState(0);
  const [embDone, setEmbDone] = useState(0);
  const [embTotal, setEmbTotal] = useState(0);
  const [result, setResult] = useState<{ surveyId: string; responses: number } | null>(null);

  // ── Step 1: upload + parse ─────────────────────────────────────────────────
  const handleFile = async (file: File) => {
    setError(null); setNotice(null); setParsing(true);
    try {
      const XLSX: any = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }) as any[][];

      const nonEmpty = aoa.filter((r) => Array.isArray(r) && r.some((c) => String(c ?? '').trim() !== ''));
      if (nonEmpty.length < 2) throw new Error('The file needs a header row and at least one response row.');

      const hdrs = (nonEmpty[0] as any[]).map((h, i) => String(h ?? '').trim() || `Column ${i + 1}`);
      let dataRows = nonEmpty.slice(1).map((r) => hdrs.map((_h, i) => r[i] ?? ''));

      if (dataRows.length > MAX_ROWS) {
        setNotice(`This file has ${dataRows.length.toLocaleString()} rows. Importing the first ${MAX_ROWS.toLocaleString()} — split the file to import the rest.`);
        dataRows = dataRows.slice(0, MAX_ROWS);
      }

      setFilename(file.name);
      setHeaders(hdrs);
      setRows(dataRows);
      setTitle({ en: file.name.replace(/\.(csv|xlsx|xls)$/i, '') });

      // AI inference on a sample only.
      let inferred: ImportColumn[];
      try {
        inferred = await inferImportSchema(hdrs, dataRows.slice(0, 30));
      } catch (e) {
        console.error('AI inference failed, falling back to text columns', e);
        setNotice('AI could not analyze the columns — defaulting everything to Text. Adjust the types below.');
        inferred = hdrs.map((header, index) => ({
          index, header, include: true, question_type: 'TEXT' as ImportQuestionType,
          question_text: { en: header }, options: [], attribute_id: null,
        }));
      }
      setColumns(inferred);
      setStep('MAPPING');
    } catch (e: any) {
      setError(e.message || 'Could not read this file.');
    } finally {
      setParsing(false);
    }
  };

  const updateColumn = (index: number, patch: Partial<ImportColumn>) =>
    setColumns((cols) => cols.map((c) => (c.index === index ? { ...c, ...patch } : c)));

  // ── Step 2 -> 3: run the import ────────────────────────────────────────────
  const startImport = async () => {
    const included = columns.filter((c) => c.include);
    if (included.length === 0) return setError('Select at least one column to import.');
    if (!title.en?.trim()) return setError('Give the imported survey a title.');

    setError(null);
    setStep('IMPORTING');
    setPhase('responses');
    setRespDone(0); setEmbDone(0);
    setRespTotal(rows.length);

    let importId: string | null = null;
    try {
      // 1. Build questions from the included columns (options reconciled against ALL rows).
      const { questions, sourceColumns } = columnsToQuestions(columns, rows);

      // 2. Create the survey (DRAFT, external provenance — never earns rewards).
      const survey = await createSurvey({
        title,
        description: { en: `Imported from ${filename}` } as LocalizedText,
        status: 'DRAFT' as any,
        reward_type: 'NONE' as any,
        max_responses: null,
        is_kiosk: false,
        require_login: false,
        source: 'IMPORT_CSV',
      } as any, questions);
      const surveyId = survey.id;

      // 3. Resolve DB question ids (ordered to match sourceColumns).
      const dbQuestions = await fetchSurveyQuestions(surveyId);
      const questionIds = dbQuestions.map((q) => q.id);

      // 4. Import batch record (audit + undo).
      importId = await createImportRecord(surveyId, filename, rows.length);

      // 5. Pre-build every row's answers.
      const allRows: ImportBatchRow[] = rows.map((r, idx) => ({
        row: idx,
        answers: sourceColumns
          .map((col, ci) => ({ questionId: questionIds[ci], value: cellToAnswerValue(col, r[col.index]) }))
          .filter((a) => a.value !== null && a.value !== undefined),
      }));
      setEmbTotal(allRows.reduce((s, r) => s + r.answers.length, 0));

      // 6. Insert in batches with a live progress bar.
      let done = 0;
      for (let i = 0; i < allRows.length; i += INSERT_BATCH) {
        const slice = allRows.slice(i, i + INSERT_BATCH);
        await importResponsesBatch(importId, surveyId, slice);
        done += slice.length;
        setRespDone(done);
      }

      // 7. Index everything for analysis, chunk by chunk.
      setPhase('embedding');
      let embedded = 0;
      for (;;) {
        const { embedded: n, remaining } = await embedPendingAnswers(surveyId, EMBED_CHUNK);
        embedded += n;
        setEmbDone(embedded);
        if (remaining === 0 || n === 0) break;
      }

      await setImportStatus(importId, 'COMPLETED');
      setResult({ surveyId, responses: allRows.length });
      setStep('DONE');
    } catch (e: any) {
      console.error(e);
      if (importId) { try { await setImportStatus(importId, 'FAILED'); } catch { /* ignore */ } }
      setError(e.message || 'Import failed. You can adjust the mapping and try again.');
      setStep('MAPPING');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      <div className="flex items-center gap-4 border-b border-slate-700 pb-4">
        <button onClick={() => navigate('/research/campaigns')} className="text-slate-400 hover:text-white transition-colors text-sm">&larr; Campaigns</button>
        <h2 className="text-xl font-bold text-white flex items-center gap-2"><FileSpreadsheet className="text-indigo-400" size={22} /> Import a survey</h2>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/30 p-3 rounded-lg flex items-start gap-2 text-sm text-red-200">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" /> {error}
        </div>
      )}
      {notice && (
        <div className="bg-amber-900/20 border border-amber-500/30 p-3 rounded-lg flex items-start gap-2 text-sm text-amber-200">
          <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" /> {notice}
        </div>
      )}

      {/* STEP 1: UPLOAD */}
      {step === 'UPLOAD' && (
        <div className="space-y-4">
          <label className="block cursor-pointer bg-slate-800 hover:bg-slate-750 border-2 border-dashed border-slate-600 hover:border-indigo-500 rounded-2xl p-12 text-center transition-all">
            {parsing ? (
              <div className="flex flex-col items-center gap-3 text-slate-300">
                <Loader2 className="animate-spin text-indigo-400" size={40} />
                <span className="font-medium">Reading file & analyzing columns…</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center text-indigo-400"><UploadCloud size={32} /></div>
                <span className="text-white font-bold text-lg">Upload a CSV or Excel file</span>
                <span className="text-slate-400 text-sm">.csv, .xlsx or .xls — one row per response</span>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
          </label>

          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-sm text-slate-300 space-y-2">
            <p className="font-semibold text-white flex items-center gap-2"><Sparkles size={15} className="text-indigo-400" /> From Google Forms?</p>
            <p className="text-slate-400">Open your form’s linked Google Sheet → <strong>File → Download → Comma-separated values (.csv)</strong>, then upload it here. The same works for Microsoft Forms, SurveyMonkey and Typeform exports.</p>
          </div>

          <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-xl p-4 text-sm text-indigo-100 flex items-start gap-2">
            <ShieldAlert size={16} className="text-indigo-300 shrink-0 mt-0.5" />
            <span>Imported responses are kept as <strong>external data</strong>: they’re labelled “Imported”, used only for analysis, and never earn rewards — so your verified first-party data stays distinct.</span>
          </div>
        </div>
      )}

      {/* STEP 2: MAPPING */}
      {step === 'MAPPING' && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Survey title</label>
            <input value={title.en || ''} onChange={(e) => setTitle({ ...title, en: e.target.value })}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-indigo-500" />
          </div>

          <div className="flex items-center justify-between text-sm text-slate-400">
            <span className="flex items-center gap-2"><Table2 size={15} /> {rows.length.toLocaleString()} responses · {columns.filter(c => c.include).length}/{columns.length} columns selected</span>
          </div>

          <div className="border border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-slate-400 text-xs uppercase">
                  <tr>
                    <th className="p-3 text-left font-medium">Import</th>
                    <th className="p-3 text-left font-medium">Column</th>
                    <th className="p-3 text-left font-medium">Type</th>
                    <th className="p-3 text-left font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {columns.map((col) => {
                    const multi = col.question_type === 'CHECKBOX';
                    const distinct = ['MULTIPLE_CHOICE', 'CHECKBOX'].includes(col.question_type)
                      ? distinctValues(rows, col.index, multi).length : 0;
                    return (
                      <tr key={col.index} className={col.include ? 'bg-slate-900/40' : 'bg-slate-900/10 opacity-50'}>
                        <td className="p-3">
                          <input type="checkbox" checked={col.include}
                            onChange={(e) => updateColumn(col.index, { include: e.target.checked })}
                            className="w-4 h-4 accent-indigo-500" />
                        </td>
                        <td className="p-3 max-w-xs">
                          <div className="text-white font-medium truncate" title={col.header}>{col.header}</div>
                          <div className="text-slate-500 text-xs truncate">
                            e.g. {String(rows[0]?.[col.index] ?? '—').slice(0, 40) || '—'}
                          </div>
                        </td>
                        <td className="p-3">
                          <select value={col.question_type} disabled={!col.include}
                            onChange={(e) => updateColumn(col.index, { question_type: e.target.value as ImportQuestionType })}
                            className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-500 disabled:opacity-40">
                            {IMPORT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                          </select>
                        </td>
                        <td className="p-3 text-xs text-slate-400">
                          {['MULTIPLE_CHOICE', 'CHECKBOX'].includes(col.question_type) && (
                            <span>{distinct} option{distinct === 1 ? '' : 's'} from data</span>
                          )}
                          {col.attribute_id && (
                            <span className="ml-2 inline-flex items-center gap-1 bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                              <Lock size={10} /> vault: {col.attribute_id}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button onClick={() => { setStep('UPLOAD'); setColumns([]); setError(null); setNotice(null); }}
              className="text-slate-400 hover:text-white flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Choose another file</button>
            <button onClick={startImport}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-6 py-2.5 rounded-lg flex items-center gap-2 transition-colors">
              Import {rows.length.toLocaleString()} responses <ArrowRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: IMPORTING */}
      {step === 'IMPORTING' && (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 space-y-6">
          <ProgressBar
            label={phase === 'responses' ? 'Importing responses' : 'Responses imported'}
            done={respDone} total={respTotal} active={phase === 'responses'} doneState={phase !== 'responses'} />
          <ProgressBar
            label="Indexing for AI analysis"
            done={embDone} total={embTotal} active={phase === 'embedding'} doneState={false}
            idle={phase === 'responses'} />
          <p className="text-slate-500 text-xs text-center">Large imports run in batches — you can leave this open; it’s safe to retry if a batch hiccups.</p>
        </div>
      )}

      {/* STEP 4: DONE */}
      {step === 'DONE' && result && (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center space-y-5">
          <CheckCircle2 size={56} className="text-emerald-400 mx-auto" />
          <div>
            <h3 className="text-2xl font-bold text-white">Import complete</h3>
            <p className="text-slate-400 mt-1">{result.responses.toLocaleString()} responses imported and indexed for analysis.</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => navigate(`/research/campaigns/${result.surveyId}/responses`)}
              className="bg-slate-700 hover:bg-slate-600 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2"><List size={16} /> View responses</button>
            <button onClick={() => navigate('/research/analytics')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2"><BarChart2 size={16} /> Analyze</button>
            <button onClick={() => { setStep('UPLOAD'); setColumns([]); setRows([]); setResult(null); setError(null); setNotice(null); }}
              className="text-slate-400 hover:text-white px-4 py-2.5 text-sm">Import another</button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProgressBar: React.FC<{ label: string; done: number; total: number; active: boolean; doneState: boolean; idle?: boolean }> =
  ({ label, done, total, active, doneState, idle }) => {
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : (doneState ? 100 : 0);
    return (
      <div className={idle ? 'opacity-40' : ''}>
        <div className="flex items-center justify-between mb-1.5 text-sm">
          <span className="text-slate-200 font-medium flex items-center gap-2">
            {active && <Loader2 size={14} className="animate-spin text-indigo-400" />}
            {doneState && <CheckCircle2 size={14} className="text-emerald-400" />}
            {label}
          </span>
          <span className="text-slate-400 text-xs font-mono">{total > 0 ? `${done.toLocaleString()} / ${total.toLocaleString()}` : (doneState ? 'done' : '—')}</span>
        </div>
        <div className="h-2.5 bg-slate-700 rounded-full overflow-hidden">
          <div className={`h-full transition-all duration-300 ${doneState ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

export default ImportSurveyView;
